import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCheck, FaTimes, FaFlag } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { logServicemanDeletion, logServicemanApproval } from "../utils/loggingWrapper";
import * as XLSX from "xlsx";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { ACTIVITY_TYPES } from "../services/activityLogger";

const STORAGE_KEY = 'servicemenListState';
const CACHE_KEY = 'servicemenDataCache';
const CACHE_DURATION = 30000; // 30 seconds cache duration

/** Backend may return stale `Approved` flags; suspended/rejoined accounts must go back to review. */
const isServicemanApproved = (s) => {
  const status = String(s?.status || s?.approvalStatus || s?.reviewStatus || "").toLowerCase();
  if (
    ["suspended", "blocked", "rejected"].includes(status) ||
    s?.wasSuspended === true ||
    s?.rejoinedAfterSuspend === true ||
    s?.suspendedAt ||
    s?.lastSuspendedAt ||
    s?.suspended_at
  ) {
    return false;
  }
  if (status.includes("pending") || status.includes("correction") || status.includes("review")) return false;
  if (status === "approved") return true;
  return !!(s?.Approved ?? s?.approved);
};

const ServicemenListWithLogging = () => {
  const [servicemen, setServicemen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFirebase, setLoadingFirebase] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const scrollContainerRef = useRef(null);
  const location = useLocation();
  const isFetchingRef = useRef(false);
  const intervalRef = useRef(null);
  const firebaseDataLoadedRef = useRef({ servicemen: false, approvalLogs: false });

  // Restore state from sessionStorage on mount
  const getStoredState = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error restoring state:', e);
    }
    return null;
  };

  // Get cached servicemen data
  const getCachedServicemen = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        // Return cached data if it's less than CACHE_DURATION old
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  };

  // Clear old cache entries to free up space
  const clearOldCache = () => {
    try {
      const cacheKeys = ['customersDataCache', 'servicemenDataCache', 'dashboardDataCache', 'dashboardStatsCache', 'bookingsDataCache'];
      cacheKeys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            const now = Date.now();
            // Clear cache older than 1 minute
            if (now - timestamp > 60000) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          // Ignore errors for individual cache entries
        }
      });
    } catch (e) {
      // Ignore errors
    }
  };

  // Save servicemen to cache with quota handling
  const saveToCache = (servicemenData) => {
    try {
      // Try to clear old cache first
      clearOldCache();
      
      // Reduce data size by only storing essential fields
      const reducedData = servicemenData.map(item => ({
        id: item.id,
        name: item.name,
        email: item.email,
        phone: item.phone,
        availabilityStart: item.availabilityStart,
        approved: item.approved,
        havePaid: item.havePaid,
        flagged: item.flagged,
        city: item.city,
        area: item.area
      }));

      const cacheData = {
        data: reducedData,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        // Clear all cache and try again
        try {
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem('customersDataCache');
          localStorage.removeItem('dashboardDataCache');
          localStorage.removeItem('bookingsDataCache');
          // Don't retry, just fail silently
        } catch (clearError) {
          // Ignore clear errors
        }
      }
      // Silently fail - app will work without cache
    }
  };

  const storedState = getStoredState();
  const [currentPage, setCurrentPage] = useState(storedState?.currentPage || 1);
  const [sortBy, setSortBy] = useState("date");
  const [approvalFilter, setApprovalFilter] = useState("All");
  const [rangeFilter, setRangeFilter] = useState("total"); // today | last7 | month | total
  const [customStartDate, setCustomStartDate] = useState(''); // yyyy-mm-dd
  const [customEndDate, setCustomEndDate] = useState(''); // yyyy-mm-dd
  // Location search state
  const [addressQuery, setAddressQuery] = useState("");
  // Approval mapping: servicemanId -> approver name
  const [approvalMap, setApprovalMap] = useState({});
  const servicemenPerPage = 20;
  const DEFAULT_IMAGE = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";
  const navigate = useNavigate();
  const { hasPermission, PERMISSIONS, user } = useAuth();

  // Check if user can see "Approved By" column - hide for sub_manager role
  const canSeeApprovedBy = user && user.role && user.role !== "sub_manager";

  // Major localities in Visakhapatnam
  const VIZAG_LOCALITIES = [
    "Dwaraka Nagar", "MVP Colony", "Seethammadhara", "Akkayyapalem", "Siripuram",
    "Maddilapalem", "Lawson's Bay Colony", "Beach Road", "Gajuwaka", "NAD Junction",
    "Pendurthi", "Marripalem", "Kancharapalem", "Resapuvanipalem", "Muralinagar",
    "Pedda Waltair", "Ram Nagar", "Jagadamba Junction", "Railway New Colony",
    "Kurmannapalem", "Akkireddypalem", "Sheela Nagar", "Scindia", "Autonagar",
    "Ukkunagaram", "Vizag Steel Plant Township", "Aganampudi", "Simhachalam",
    "Gopalapatnam", "Sujatha Nagar", "Yendada", "Madhurawada", "Rushikonda",
    "Venkojipalem", "Isukathota", "Balayya Sastri Layout", "Lawson's Bay",
    "Arilova", "Kommadi", "Pothinamallayya Palem", "PM Palem", "Anandapuram",
    // Common variations and abbreviations
    "Dwarka Nagar", "Dwarkanagar", "MVP", "Lawson Bay", "Lawson Bay Colony",
    "NAD", "Jagadamba", "Steel Plant", "Vizag Steel", "PM Palem"
  ].map(loc => loc.toLowerCase());

  const checkFirebaseDataComplete = () => {
    if (firebaseDataLoadedRef.current.servicemen && firebaseDataLoadedRef.current.approvalLogs) {
      // Add minimum display time to ensure loader is visible
      setTimeout(() => {
        setLoadingFirebase(false);
      }, 300);
    }
  };

  const fetchServicemen = async (showLoader = true) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      firebaseDataLoadedRef.current.servicemen = false;
      if (showLoader) {
        setLoading(true);
      }

      const response = await fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen");
      const data = await response.json();
      const servicemenArray = Array.isArray(data) ? data : [data];
      
      setServicemen(servicemenArray);
      saveToCache(servicemenArray);
      
      firebaseDataLoadedRef.current.servicemen = true;
      checkFirebaseDataComplete();
      
      if (showLoader) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching servicemen:", error);
      // Don't clear servicemen on error if we have cached data
      const cached = getCachedServicemen();
      if (!cached) {
        setServicemen([]);
      }
      firebaseDataLoadedRef.current.servicemen = true;
      checkFirebaseDataComplete();
      if (showLoader) {
        setLoading(false);
      }
    } finally {
      isFetchingRef.current = false;
    }
  };

  const fetchApprovalLogs = async () => {
    try {
      firebaseDataLoadedRef.current.approvalLogs = false;
      // Query logs collection for serviceman approval entries
      // Fetch without orderBy to avoid index requirement, then sort in memory
      const logsQuery = query(
        collection(db, 'logs'),
        where('activityType', '==', ACTIVITY_TYPES.SERVICEMAN_APPROVED)
      );

      const logsSnapshot = await getDocs(logsQuery);
      const logsWithTimestamps = [];

      logsSnapshot.forEach(doc => {
        const logData = doc.data();
        const servicemanId = logData.details?.servicemanId;
        const approverName = logData.userName || logData.username || 'Unknown';

        // Extract timestamp - handle Firestore Timestamp or ISO string
        let timestamp = 0;
        if (logData.timestamp) {
          if (logData.timestamp.toDate) {
            timestamp = logData.timestamp.toDate().getTime();
          } else if (logData.timestamp._seconds) {
            timestamp = logData.timestamp._seconds * 1000;
          } else if (logData.timestamp.seconds) {
            timestamp = logData.timestamp.seconds * 1000;
          }
        } else if (logData.createdAt) {
          timestamp = new Date(logData.createdAt).getTime();
        }

        if (servicemanId) {
          logsWithTimestamps.push({
            servicemanId,
            approverName,
            timestamp
          });
        }
      });

      // Sort by timestamp descending (most recent first)
      logsWithTimestamps.sort((a, b) => b.timestamp - a.timestamp);

      // Create mapping, keeping only the most recent approval per serviceman
      const approvalMapping = {};
      logsWithTimestamps.forEach(log => {
        if (!approvalMapping[log.servicemanId]) {
          approvalMapping[log.servicemanId] = log.approverName;
        }
      });

      setApprovalMap(approvalMapping);
      firebaseDataLoadedRef.current.approvalLogs = true;
      checkFirebaseDataComplete();
    } catch (error) {
      console.error("Error fetching approval logs:", error);
      // Set empty mapping on failure
      setApprovalMap({});
      firebaseDataLoadedRef.current.approvalLogs = true;
      checkFirebaseDataComplete();
    }
  };

  // Save state to sessionStorage
  const saveState = () => {
    try {
      const state = {
        currentPage,
        scrollPosition: scrollContainerRef.current?.scrollTop || window.scrollY || 0
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Error saving state:', e);
    }
  };

  // Save scroll position on scroll
  useEffect(() => {
    const container = scrollContainerRef.current || window;
    const handleScroll = () => {
      const scrollPos = scrollContainerRef.current?.scrollTop || window.scrollY || 0;
      try {
        const state = {
          currentPage,
          scrollPosition: scrollPos
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // Ignore storage errors
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  // Save state when page changes
  useEffect(() => {
    saveState();
  }, [currentPage]);

  useEffect(() => {
    // Reset Firebase data loaded flags
    firebaseDataLoadedRef.current = { servicemen: false, approvalLogs: false };
    
    // Load cached data immediately for instant display
    const cachedServicemen = getCachedServicemen();
    if (cachedServicemen) {
      setServicemen(cachedServicemen);
      setLoading(false);
      // Show overlay loader while fetching Firebase data
      setLoadingFirebase(true);
      // Fetch fresh data in background without showing loader
      fetchServicemen(false);
    } else {
      // No cache, fetch with loader
      setLoadingFirebase(true);
      fetchServicemen(true);
    }

    // Fetch approval logs (this is separate and doesn't need caching)
    // This will set loadingFirebase to false when done (along with fetchServicemen)
    fetchApprovalLogs();

    // Smart polling: only poll when tab is visible and reduce frequency
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, clear interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        // Tab is visible, start polling
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            fetchServicemen(false); // Background refresh without loader
            fetchApprovalLogs(); // Also refresh approval logs
          }, 30000); // Poll every 30 seconds instead of 5
        }
      }
    };

    // Start polling if tab is visible
    if (!document.hidden) {
      intervalRef.current = setInterval(() => {
        fetchServicemen(false); // Background refresh without loader
        fetchApprovalLogs(); // Also refresh approval logs
      }, 30000); // Poll every 30 seconds instead of 5
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && storedState) {
      // Use setTimeout to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const scrollPos = storedState.scrollPosition || 0;
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPos;
        } else {
          window.scrollTo(0, scrollPos);
        }
        // Clear the stored state after restoring
        sessionStorage.removeItem(STORAGE_KEY);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading, storedState]);

  // Pre-fill search from ?phone= or ?id= when opened from Account Delete Requests (e.g. new tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phone = params.get("phone");
    const id = params.get("id");
    if (phone != null && String(phone).trim() !== "") {
      setSearchTerm(String(phone).trim());
      setCurrentPage(1);
    } else if (id != null && String(id).trim() !== "") {
      setSearchTerm(String(id).trim());
      setCurrentPage(1);
    }
  }, [location.search]);

  // Helper function to convert Firebase Timestamp to Date
  const toDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    // Handle Firestore Timestamp with toDate method
    if (val && typeof val.toDate === 'function') return val.toDate();
    if (typeof val === "number") return new Date(val);
    if (typeof val === "string") {
      // Try parsing the string format "22 December 2025 at 21:28:38 UTC+5:30"
      // Replace "at" with a space and try to parse
      const normalizedStr = val.replace(/\s+at\s+/i, ' ');
      const d = new Date(normalizedStr);
      if (!isNaN(d.getTime())) return d;
      // Fallback to original string parsing
      const d2 = new Date(val);
      return isNaN(d2.getTime()) ? null : d2;
    }
    // Firestore timestamp formats
    if (val._seconds) return new Date(val._seconds * 1000);
    if (val.seconds) return new Date(val.seconds * 1000);
    if (val.toMillis) return new Date(val.toMillis());
    if (val.createdAt?._seconds) return new Date(val.createdAt._seconds * 1000);
    return null;
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  // Helper function to format date as DD-MM-YYYY
  const formatDateOnly = (val) => {
    const date = toDate(val);
    if (!date) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Compute effective date range based on rangeFilter and optional custom dates (same as Dashboard)
  const getEffectiveRange = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const defaultRanges = {
      today: { start: startOfToday, end: new Date(startOfToday.getTime() + dayMs) },
      last7: { start: new Date(startOfToday.getTime() - 6 * dayMs), end: new Date(startOfToday.getTime() + dayMs) },
      month: { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 1) },
      total: { start: new Date(0), end: new Date(8640000000000000) },
    };

    let { start, end } = defaultRanges[rangeFilter] || defaultRanges.total;

    // If last7, month, or total and custom dates provided, override
    if ((rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total')) {
      const hasStart = customStartDate && customStartDate.trim() !== '';
      const hasEnd = customEndDate && customEndDate.trim() !== '';
      if (hasStart) {
        const s = new Date(customStartDate + 'T00:00:00');
        if (!isNaN(s.getTime())) start = s;
      }
      if (hasEnd) {
        const e = new Date(customEndDate + 'T23:59:59.999');
        if (!isNaN(e.getTime())) end = e;
      }
    }
    return { start, end };
  };

  // Helper function to filter by date period (updated to use getEffectiveRange)
  const filterByDate = (serviceman) => {
    // If rangeFilter is "total", show all servicemen regardless of date
    if (rangeFilter === "total") {
      return true;
    }

    // Use createdAt or profileUpdatedAt to filter by registration date
    // availabilityStart is for work hours, not registration date
    const servicemanDate = toDate(serviceman.createdAt) || toDate(serviceman.profileUpdatedAt);

    // If no date is available, exclude from date-based filters (today, last7, month)
    if (!servicemanDate) {
      return false;
    }

    const { start, end } = getEffectiveRange();
    return servicemanDate >= start && servicemanDate < end;
  };

  // Haversine distance in KM (declare before use)
  function haversineDistanceKm(lat1, lon1, lat2, lon2) {
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  const filteredServicemen = useMemo(() => {
    return servicemen
      .filter((serviceman) => {
        // Phone number search - normalize phone numbers by removing spaces, dashes, and other non-digit characters
        const normalizePhone = (phone) => {
          if (!phone) return "";
          // Convert to string, remove all non-digit characters, and trim
          return String(phone).replace(/\D/g, "").trim();
        };

        const phoneStr = normalizePhone(serviceman.phone);
        const searchStr = normalizePhone(searchTerm);
        // Match if search term is found anywhere in the phone number (partial match)
        const phoneMatches = searchStr.length > 0 && phoneStr.length > 0 && phoneStr.includes(searchStr);

        // Also check if search term (as-is) matches phone number (for exact matches with formatting)
        const phoneMatchesFormatted = searchTerm.trim().length > 0 &&
          String(serviceman.phone || "").includes(searchTerm.trim());

        const matchesSearch =
          (serviceman.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (serviceman.profession || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (serviceman.id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          phoneMatches ||
          phoneMatchesFormatted;
        const matchesApproval =
          approvalFilter === "All" ||
          (approvalFilter === "Approved" && isServicemanApproved(serviceman)) ||
          (approvalFilter === "Not Approved" && !isServicemanApproved(serviceman));

        // Filter for "Not Paid & Not Approved" when sortBy is set to that option
        let matchesNotPaidAndNotApproved = true;
        if (sortBy === "notPaidAndNotApproved") {
          const isNotApproved = !isServicemanApproved(serviceman);
          const isNotPaid = serviceman.havePaid === false || 
                           serviceman.havePaid === 'false' || 
                           serviceman.havePaid === 'False' ||
                           serviceman.havePaid === null ||
                           serviceman.havePaid === undefined ||
                           !serviceman.hasOwnProperty('havePaid');
          matchesNotPaidAndNotApproved = isNotApproved && isNotPaid;
        }

        // Date filter
        const matchesDate = filterByDate(serviceman);

        // Location filter - real-time text matching as user types
        let matchesLocation = true;

        // Text-based area matching (if addressQuery is provided)
        if (addressQuery && addressQuery.trim() !== '') {
          const queryLower = addressQuery.trim().toLowerCase();

          // Get areaName (case-insensitive) - trim and check if it contains the query
          const servicemanAreaName = serviceman.areaName
            ? String(serviceman.areaName).trim().toLowerCase()
            : "";

          // Get address/location fields (case-insensitive) - combine all location fields
          const servicemanLocation = (
            (serviceman.location || "") + " " +
            (serviceman.address || "") + " " +
            (serviceman.area || "")
          ).trim().toLowerCase();

          // Check if typed keyword is contained in areaName
          const areaNameMatches = servicemanAreaName.length > 0 &&
            servicemanAreaName.includes(queryLower);

          // Check if typed keyword is contained in address/location
          const addressMatches = servicemanLocation.length > 0 &&
            servicemanLocation.includes(queryLower);

          // Show results if either areaName OR address matches
          matchesLocation = areaNameMatches || addressMatches;
        }

        return matchesSearch && matchesApproval && matchesNotPaidAndNotApproved && matchesDate && matchesLocation;
      })
      .sort((a, b) => {
        if (sortBy === "id") return a.id.localeCompare(b.id);
        if (sortBy === "avgRating") return (b.avgRating || 0) - (a.avgRating || 0);
        if (sortBy === "accountEdited") {
          const aEdited = a.accountEdited === true;
          const bEdited = b.accountEdited === true;
          // Edited accounts first
          if (aEdited && !bEdited) return -1;
          if (!aEdited && bEdited) return 1;
          // If same status, sort by name for stability
          return (a.name || "").localeCompare(b.name || "");
        }
        if (sortBy === "device") {
          const aDevice = a.deviceType || "";
          const bDevice = b.deviceType || "";

          // Handle N/A to appear last
          if (aDevice === "" || aDevice === "N/A") return 1;
          if (bDevice === "" || bDevice === "N/A") return -1;

          return aDevice.localeCompare(bDevice);
        }
        if (sortBy === "flagged") {
          // Check if servicemen are flagged (handle different data types)
          const aFlagged = a.isFlaged === true || a.isFlaged === 'true' || a.isFlaged === 'True';
          const bFlagged = b.isFlaged === true || b.isFlaged === 'true' || b.isFlaged === 'True';

          // Flagged servicemen appear first
          if (aFlagged && !bFlagged) return -1;
          if (!aFlagged && bFlagged) return 1;

          // If both have same flag status, sort by name as secondary sort
          return (a.name || "").localeCompare(b.name || "");
        }
        if (sortBy === "date") {
          const aDate = toDate(a.createdAt);
          const bDate = toDate(b.createdAt);

          // Servicemen without dates appear last
          if (!aDate && !bDate) return (a.name || "").localeCompare(b.name || "");
          if (!aDate) return 1;
          if (!bDate) return -1;

          // Sort by newest first (descending order)
          return bDate.getTime() - aDate.getTime();
        }
        if (sortBy === "isVerified") {
          const aVerified = a.isVerified === true;
          const bVerified = b.isVerified === true;

          // Verified servicemen appear first
          if (aVerified && !bVerified) return -1;
          if (!aVerified && bVerified) return 1;

          // If both have same status, sort by name as secondary sort
          return (a.name || "").localeCompare(b.name || "");
        }
        if (sortBy === "notPaidAndNotApproved") {
          // Check if servicemen are not approved
          const aNotApproved = !isServicemanApproved(a);
          const bNotApproved = !isServicemanApproved(b);
          
          // Check if servicemen have paid (handle different data types)
          const aPaid = a.havePaid === true || a.havePaid === 'true' || a.havePaid === 'True';
          const bPaid = b.havePaid === true || b.havePaid === 'true' || b.havePaid === 'True';
          const aNotPaid = !aPaid && (a.havePaid === false || a.havePaid === 'false' || a.havePaid === 'False' || a.havePaid === null || a.havePaid === undefined || !a.hasOwnProperty('havePaid'));
          const bNotPaid = !bPaid && (b.havePaid === false || b.havePaid === 'false' || b.havePaid === 'False' || b.havePaid === null || b.havePaid === undefined || !b.hasOwnProperty('havePaid'));
          
          // Both conditions must be true (not approved AND not paid)
          const aMatches = aNotApproved && aNotPaid;
          const bMatches = bNotApproved && bNotPaid;
          
          // Matching accounts appear first
          if (aMatches && !bMatches) return -1;
          if (!aMatches && bMatches) return 1;

          // If both match or both don't match, sort by name as secondary sort
          return (a.name || "").localeCompare(b.name || "");
        }
        return (a[sortBy] || "").localeCompare(b[sortBy] || "");
      });
  }, [servicemen, searchTerm, sortBy, approvalFilter, rangeFilter, customStartDate, customEndDate, addressQuery]);


  const handleLocationSearchChange = (e) => {
    setAddressQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const clearLocationSearch = () => {
    setAddressQuery("");
    setCurrentPage(1);
  };

  const paginatedServicemen = useMemo(() => {
    const indexOfLastServiceman = currentPage * servicemenPerPage;
    const indexOfFirstServiceman = indexOfLastServiceman - servicemenPerPage;
    return {
      indexOfFirstServiceman,
      currentServicemen: filteredServicemen.slice(indexOfFirstServiceman, indexOfLastServiceman),
      totalPages: Math.ceil(filteredServicemen.length / servicemenPerPage),
    };
  }, [filteredServicemen, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of table when page changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate pagination page numbers with sliding window
  const getPaginationPages = () => {
    const totalPages = paginatedServicemen.totalPages;
    const current = currentPage;
    const maxVisible = 10; // Maximum number of page buttons to show
    const pages = [];

    if (totalPages <= maxVisible) {
      // If total pages is less than max visible, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate window around current page
      let start = Math.max(2, current - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages - 1, start + maxVisible - 3);

      // Adjust start if we're near the end
      if (end === totalPages - 1) {
        start = Math.max(2, totalPages - maxVisible + 2);
      }

      // Add ellipsis if there's a gap after first page
      if (start > 2) {
        pages.push('ellipsis-start');
      }

      // Add pages in the window
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if there's a gap before last page
      if (end < totalPages - 1) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  const handleApprovalFilterChange = (e) => {
    setApprovalFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleView = (serviceman) => {
    saveState();
    navigate("/serviceman-profile", { state: { serviceman, fromList: true } });
  };

  const handleDelete = async (servicemanId) => {
    if (!window.confirm("Are you sure you want to delete this serviceman?")) return;

    try {
      // Find the serviceman to get their name for logging
      const servicemanToDelete = servicemen.find(s => s.id === servicemanId);
      const servicemanName = servicemanToDelete?.name || 'Unknown';

      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}`,
        { method: "DELETE" }
      );

      if (!response.ok) throw new Error("Failed to delete serviceman");

      // Update local state
      setServicemen((prev) => prev.filter((serviceman) => serviceman.id !== servicemanId));

      // Log the activity
      await logServicemanDeletion(user, servicemanId, servicemanName);

      alert("Serviceman deleted successfully");
    } catch (error) {
      console.error("Error deleting serviceman:", error);
      alert("Failed to delete serviceman");
    }
  };

  const handleReviews = (servicemanId) => navigate("/serviceman-reviews", { state: { servicemanId } });

  const handleApproval = async (servicemanId, currentApproval) => {
    const newApprovalStatus = !currentApproval;
    const action = newApprovalStatus ? "approve" : "reject";

    if (!window.confirm(`Are you sure you want to ${action} this serviceman?`)) return;

    try {
      // Find the serviceman to get their name for logging
      const servicemanToUpdate = servicemen.find(s => s.id === servicemanId);
      const servicemanName = servicemanToUpdate?.name || 'Unknown';

      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}/approve`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Approved: newApprovalStatus,
            approvedBy: newApprovalStatus ? (user?.username || user?.name || "Unknown") : null,
          }),
        }
      );

      if (!response.ok) throw new Error(`Failed to ${action} serviceman`);

      // Update local state (include approvedBy when approving so UI shows it)
      const approverName = newApprovalStatus ? (user?.username || user?.name || "Unknown") : null;
      setServicemen((prev) =>
        prev.map((serviceman) =>
          serviceman.id === servicemanId
            ? {
                ...serviceman,
                Approved: newApprovalStatus,
                approved: newApprovalStatus,
                approvedBy: approverName,
              }
            : serviceman
        )
      );

      // Log the activity
      await logServicemanApproval(user, servicemanId, servicemanName, newApprovalStatus);

      alert(`Serviceman ${action}d successfully`);
    } catch (error) {
      console.error(`Error ${action}ing serviceman:`, error);
      alert(`Failed to ${action} serviceman`);
    }
  };

  const handleResetAccountEdited = async (servicemanId) => {
    if (!window.confirm("Set accountEdited to false for this serviceman?")) return;

    try {
      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}/accountEdited`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountEdited: false }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reset accountEdited");
      }

      setServicemen((prev) =>
        prev.map((s) =>
          s.id === servicemanId ? { ...s, accountEdited: false } : s
        )
      );
      alert("accountEdited set to false successfully");
    } catch (error) {
      console.error("Error resetting accountEdited for serviceman:", error);
      alert("Failed to reset accountEdited");
    }
  };

  const handleFlag = async (servicemanId, isCurrentlyFlagged) => {
    const action = isCurrentlyFlagged ? "unflag" : "flag";

    // Only show confirmation when unflagging (removing the flag)
    if (isCurrentlyFlagged) {
      const confirmMessage = "Are you sure you want to unflag this serviceman? This will remove the flag status.";
      if (!window.confirm(confirmMessage)) return;
    }

    try {
      // Find the serviceman to verify it exists
      const servicemanToUpdate = servicemen.find(s => s.id === servicemanId);
      if (!servicemanToUpdate) {
        throw new Error("Serviceman not found");
      }

      // Only send the isFlaged field for update
      const updateData = isCurrentlyFlagged
        ? { isFlaged: null } // Send null to remove the field
        : { isFlaged: true };

      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to ${action} serviceman`);
      }

      // Update local state
      setServicemen((prev) =>
        prev.map((serviceman) => {
          if (serviceman.id === servicemanId) {
            const updated = { ...serviceman };
            if (isCurrentlyFlagged) {
              // Remove the isFlaged field
              delete updated.isFlaged;
            } else {
              // Add the isFlaged field
              updated.isFlaged = true;
            }
            return updated;
          }
          return serviceman;
        })
      );

      alert(`Serviceman ${action}ed successfully`);
    } catch (error) {
      console.error(`Error ${action}ing serviceman:`, error);
      alert(`Failed to ${action} serviceman: ${error.message}`);
    }
  };

  const handleExport = () => {
    try {
      // Get the currently filtered and sorted data
      const dataToExport = filteredServicemen.map((serviceman, index) => ({
        "Serial No": index + 1,
        "Name": serviceman.name || "N/A",
        "Email": serviceman.email || "N/A",
        "Profession": serviceman.profession || "N/A",
        "Phone": serviceman.phone || "N/A",
        "Location": serviceman.location || serviceman.address || "N/A",
        "Main Area": serviceman.areaName || "N/A",
        "Rating": serviceman.avgRating ? `${serviceman.avgRating}/5` : "N/A",
        "Device Type": serviceman.deviceType && serviceman.deviceType.trim() !== '' ? serviceman.deviceType : "N/A",
        "Have Paid": serviceman.havePaid === true || serviceman.havePaid === 'true' ? 'Yes' : 'No',
        "Amount Paid": serviceman.amountPaid !== undefined && serviceman.amountPaid !== null ? serviceman.amountPaid : 'N/A'
      }));

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataToExport);

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, "Servicemen");

      // Generate filename with current date
      const filename = `Servicemen_Export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write the file
      XLSX.writeFile(wb, filename);

      alert(`Exported ${dataToExport.length} records to ${filename}`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data");
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <span className="loader"></span>
        <style>{`
          .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            color: #666;
          }
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      {loadingFirebase && (
        <div className="loading-overlay">
          <span className="loader"></span>
          <style>{`
            .loading-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              width: 100vw;
              height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: rgba(0, 0, 0, 0.7);
              z-index: 6;
              color: var(--text-secondary, #666);
            }
            
            [data-theme="dark"] .loading-overlay {
              background-color: rgba(0, 0, 0, 0.85);
            }
            
            [data-theme="light"] .loading-overlay {
              background-color: rgba(0, 0, 0, 0.7);
            }

            .loader {
              width: 48px;
              height: 48px;
              border-radius: 50%;
              display: inline-block;
              position: relative;
              border: 2px solid #FF3D00;
              box-sizing: border-box;
              animation: rotation 1s linear infinite;
            }
            .loader::after {
              content: '';  
              box-sizing: border-box;
              position: absolute;
              left: 4px;
              top: 4px;
              border: 2px solid #000;
              width: 12px;
              height: 12px;
              border-radius: 50%;
            }
            
            [data-theme="dark"] .loader::after {
              border: 2px solid #FFF;
            }
            
            @keyframes rotation {
              0% {
                transform: rotate(0deg);
              }
              100% {
                transform: rotate(360deg);
              }
            }
          `}</style>
        </div>
      )}
    <div className="servicemen-panel" ref={scrollContainerRef}>
      <style>{`
        .servicemen-panel {
          padding: 30px;
          margin: 0 auto;
          background: #f9fafb;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
          gap: 15px;
        }
        h2 {
          color: #1f2937;
          font-size: 20px;
          margin: 0;
        }
        .search-sort {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        .location-row { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-top:8px; }
        .address-input { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; min-width: 280px; }
        .address-input:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 4px rgba(59,130,246,0.3); }
        .search-btn { padding:8px 14px; border:1px solid #10b981; border-radius:4px; background:#10b981; color:#fff; cursor:pointer; font-size:14px; }
        .search-btn:disabled { opacity:0.6; cursor:not-allowed; }
        .clear-btn { padding:8px 12px; border:1px solid #d1d5db; border-radius:4px; background:#fff; cursor:pointer; font-size:14px; }
        .search-input {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
          width: 200px;
          transition: all 0.2s;
        }
        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 4px rgba(59,130,246,0.3);
        }
        .sort-btn, .approval-filter {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: #fff;
          font-size: 14px;
          cursor: pointer;
          color: #374151;
        }
        .export-btn {
          padding: 8px 16px;
          border: 1px solid #3b82f6;
          border-radius: 4px;
          background: #3b82f6;
          font-size: 14px;
          cursor: pointer;
          color: #fff;
          font-weight: 500;
          transition: all 0.2s;
        }
        .export-btn:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
        .table-container {
          overflow-x: auto;
          min-height: 300px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          background: #f3f4f6;
          color: #4a5568;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid #e5e7eb;
          position: sticky;
          top: 0;
        }
        td {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          font-size: 14px;
          color: #4a5568;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .profile-pic {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 8px;
          vertical-align: middle;
          display: inline-block;
        }
        .status {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          display: inline-block;
        }
        .status.active {
          background: #dcfce7;
          color: #166534;
        }
        .status.inactive {
          background: #fee2e2;
          color: #991b1b;
        }
        .serviceman-row {
          transition: background 0.2s;
        }
        .serviceman-row:hover {
          background: #f9fafb;
        }
        .flagged-row {
          background-color: #fef3c7 !important;
        }
        .flagged-row:hover {
          background-color: #fde68a !important;
        }
        .flagged-row td {
          background-color: #fef3c7 !important;
          color: #000000 !important;
        }
        .flagged-row:hover td {
          background-color: #fde68a !important;
          color: #000000 !important;
        }
        [data-theme="dark"] .flagged-row {
          background-color: #78350f !important;
        }
        [data-theme="dark"] .flagged-row:hover {
          background-color: #92400e !important;
        }
        [data-theme="dark"] .flagged-row td {
          background-color: #78350f !important;
          color: #ffffff !important;
        }
        [data-theme="dark"] .flagged-row:hover td {
          background-color: #92400e !important;
          color: #ffffff !important;
        }
        .rating {
          color: #f59e0b;
          font-weight: 500;
        }
        /* Keep <td> as table-cell; flex on <td> breaks row height vs multi-line cells */
        .actions-cell {
          vertical-align: middle;
          max-width: none;
          min-width: 200px;
          overflow: visible;
          white-space: normal;
        }
        .actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .action-btn {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid #d1d5db;
          background: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2px;
          transition: background 0.3s ease;
        }
        .action-btn.view-btn:hover {
          background: #00FF00;
        }
        .action-btn.view-btn:hover svg {
          stroke: #fff;
        }
        .action-btn.delete-btn:hover {
          background: #FF0000;
        }
        .action-btn.delete-btn:hover svg {
          stroke: #fff;
        }
        .action-btn.reviews-btn:hover {
          background: #0000FF;
        }
        .action-btn.reviews-btn:hover svg {
          stroke: #fff;
        }
        .action-btn.approve-btn:not(.approved) {
          background: #dcfce7;
          color: #166534;
        }
        .action-btn.approve-btn.approved {
          background: #fee2e2;
          color: #991b1b;
        }
        .action-btn.approve-btn:not(.approved):hover {
          background: #166534;
          color: #fff;
        }
        .action-btn.approve-btn.approved:hover {
          background: #991b1b;
          color: #fff;
        }
        .action-btn.flag-btn {
          background: #fff;
          color: #6b7280;
          border: 1px solid #d1d5db;
        }
        .action-btn.flag-btn.flagged {
          background: #fef3c7;
          color: #f59e0b;
          border: 1px solid #f59e0b;
        }
        .action-btn.flag-btn:hover {
          background: #f59e0b;
          color: #fff;
          border-color: #f59e0b;
        }
        .action-btn.flag-btn.flagged:hover {
          background: #d97706;
          color: #fff;
          border-color: #d97706;
        }
        .pagination {
          margin-top: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #4a5568;
          font-size: 14px;
        }
        .pagination-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          max-width: 100%;
        }
        .page-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: #fff;
          cursor: pointer;
          color: #374151;
          transition: background 0.3s ease;
          min-width: 40px;
          text-align: center;
          white-space: nowrap;
        }
        .page-btn:hover:not(.active):not(:disabled) {
          background: #e5e7eb;
        }
        .page-btn.active {
          background: #3b82f6;
          color: #fff;
          border-color: #3b82f6;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .page-ellipsis {
          padding: 6px 4px;
          color: #6b7280;
          font-weight: 500;
          user-select: none;
        }
        .no-results {
          text-align: center;
          padding: 20px;
          color: #6b7280;
        }
        @media (max-width: 768px) {
          .header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }
          .search-sort {
            width: 100%;
          }
          .search-input, .sort-btn, .approval-filter, .export-btn {
            width: 100%;
          }
        }
      `}</style>

      <div className="header">
        <h2>Servicemen List</h2>
        <div className="search-sort">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, profession, or phone..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when search changes
            }}
          />
          <select className="sort-btn" value={sortBy} onChange={handleSortChange}>
            <option value="id">Sort By ID</option>
            <option value="name">Sort By Name</option>
            <option value="profession">Sort By Profession</option>
            <option value="avgRating">Sort By Rating</option>
            <option value="device">Sort By Device</option>
            <option value="flagged">Sort By Flagged</option>
            <option value="accountEdited">Sort By Account Edited</option>
            <option value="date">Sort By Date</option>
            <option value="isVerified">Sort By Verification Badge</option>
            <option value="notPaidAndNotApproved">Sort By Not Paid & Not Approved</option>
          </select>
          <select className="approval-filter" value={approvalFilter} onChange={handleApprovalFilterChange}>
            <option value="All">All</option>
            <option value="Approved">Approved</option>
            <option value="Not Approved">Not Approved</option>
          </select>
          {(rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total') && (
            <>
              <input
                type="date"
                className="sort-btn"
                value={customStartDate}
                onChange={(e) => { setCustomStartDate(e.target.value); setCurrentPage(1); }}
                style={{ width: '150px' }}
                placeholder="Start Date"
              />
              <input
                type="date"
                className="sort-btn"
                value={customEndDate}
                onChange={(e) => { setCustomEndDate(e.target.value); setCurrentPage(1); }}
                style={{ width: '150px' }}
                placeholder="End Date"
              />
            </>
          )}
          <select className="sort-btn" value={rangeFilter} onChange={(e) => { setRangeFilter(e.target.value); setCurrentPage(1); }}>
            <option value="today">Today</option>
            <option value="last7">Last 7 Days</option>
            <option value="month">This Month</option>
            <option value="total">Total</option>
          </select>
          <button className="export-btn" onClick={handleExport} title="Export to Excel">
            Export
          </button>
        </div>
        <div className="location-row">
          <input
            type="text"
            className="address-input"
            placeholder="Search by area name or address (e.g., Dwaraka Nagar, MVP Colony, Seethammadhara...)"
            value={addressQuery}
            onChange={handleLocationSearchChange}
          />
          {addressQuery && (
            <button className="clear-btn" onClick={clearLocationSearch}>Clear</button>
          )}
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Serviceman</th>
              <th>Profession</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Main Area</th>
              <th>Rating</th>
              <th>Device Type</th>
              <th>Date Added</th>
              <th>Payment</th>
              {canSeeApprovedBy && <th>Approved By</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedServicemen.currentServicemen.length > 0 ? (
              paginatedServicemen.currentServicemen.map((serviceman, index) => {
                const imageSrc = serviceman.image || DEFAULT_IMAGE;

                const isFlagged = serviceman.isFlaged === true || serviceman.isFlaged === 'true' || serviceman.isFlaged === 'True';

                return (
                  <tr key={serviceman.id} className={`serviceman-row ${isFlagged ? 'flagged-row' : ''}`}>
                    <td>{paginatedServicemen.indexOfFirstServiceman + index + 1}</td>
                    <td style={{ maxWidth: '250px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                          src={imageSrc}
                          alt={`${serviceman.name || "Serviceman"} profile`}
                          className="profile-pic"
                          onError={(e) => (e.target.src = DEFAULT_IMAGE)}
                          style={{ flexShrink: 0 }}
                        />
                        <span style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flexShrink: 1
                        }}>
                          {serviceman.name || "N/A"}
                        </span>
                        {!!serviceman.isVerified && (
                          <img
                            src="/blue tick.png"
                            alt="Verified"
                            style={{
                              width: '18px',
                              height: '18px',
                              flexShrink: 0,
                              display: 'inline-block',
                              marginLeft: '2px'
                            }}
                            title="Verified"
                          />
                        )}
                      </div>
                    </td>
                    <td>{serviceman.profession || "N/A"}</td>
                    <td>{serviceman.phone || "N/A"}</td>
                    <td>{serviceman.location || serviceman.address || "N/A"}</td>
                    <td>{serviceman.areaName || "N/A"}</td>
                    <td>
                      <span className="rating">
                        {serviceman.avgRating ? `${serviceman.avgRating}/5` : "N/A"}
                      </span>
                    </td>
                    <td>{serviceman.deviceType && serviceman.deviceType.trim() !== '' ? serviceman.deviceType : "N/A"}</td>
                    <td>{formatDateOnly(serviceman.createdAt)}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span>
                          <strong>Paid:</strong> {serviceman.havePaid === true || serviceman.havePaid === 'true' ? 'Yes' : 'No'}
                        </span>
                        <span>
                          <strong>Amount:</strong> {serviceman.amountPaid !== undefined && serviceman.amountPaid !== null ? `₹${serviceman.amountPaid}` : 'N/A'}
                        </span>
                      </div>
                    </td>
                    {canSeeApprovedBy && (
                      <td>
                        {isServicemanApproved(serviceman) && (serviceman.approvedBy || approvalMap[serviceman.id])
                          ? (serviceman.approvedBy || approvalMap[serviceman.id])
                          : "N/A"}
                      </td>
                    )}
                    <td className="actions-cell">
                      <div className="actions">
                      {hasPermission(PERMISSIONS.SERVICEMEN.VIEW) && (
                        <button className="action-btn view-btn" onClick={() => handleView(serviceman)} title="View">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#00FF00"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="view"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      )}
                      {hasPermission(PERMISSIONS.SERVICEMEN.DELETE) && (
                        <button className="action-btn delete-btn" onClick={() => handleDelete(serviceman.id)} title="Delete">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#FF0000"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="delete"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      )}
                      {hasPermission(PERMISSIONS.SERVICEMEN.REVIEW) && (
                        <button className="action-btn reviews-btn" onClick={() => handleReviews(serviceman.id)} title="Reviews">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#0000FF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="reviews"
                          >
                            <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
                            <path d="M6 8h12" />
                            <path d="M6 12h12" />
                            <path d="M6 16h6" />
                          </svg>
                        </button>
                      )}
                      {/* Show reset accountEdited button only when field is true */}
                      {serviceman.accountEdited === true && (
                        <button
                          className="action-btn reset-btn"
                          onClick={() => handleResetAccountEdited(serviceman.id)}
                          title="Set accountEdited to false"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#F59E0B"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-label="reset-account-edited"
                          >
                            <polyline points="1 4 1 10 7 10" />
                            <path d="M3.51 15a9 9 0 1 0 .49-5" />
                          </svg>
                        </button>
                      )}
                      {hasPermission(PERMISSIONS.SERVICEMEN.APPROVE) && (
                        <button
                          className={`action-btn approve-btn ${isServicemanApproved(serviceman) ? "approved" : ""}`}
                          onClick={() => handleApproval(serviceman.id, isServicemanApproved(serviceman))}
                          title={isServicemanApproved(serviceman) ? "Reject" : "Approve"}
                        >
                          {isServicemanApproved(serviceman) ? <FaTimes /> : <FaCheck />}
                        </button>
                      )}
                      <button
                        className={`action-btn flag-btn ${isFlagged ? "flagged" : ""}`}
                        onClick={() => handleFlag(serviceman.id, isFlagged)}
                        title={isFlagged ? "Unflag" : "Flag"}
                      >
                        <FaFlag />
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="no-results">
                  No servicemen found matching your search
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <span>Showing {filteredServicemen.length} Entries →</span>
        <div className="pagination-buttons">
          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {getPaginationPages().map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <span key={`ellipsis-${index}`} className="page-ellipsis">
                  ...
                </span>
              );
            }
            return (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? "active" : ""}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            );
          })}
          <button
            className="page-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === paginatedServicemen.totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default ServicemenListWithLogging;
